<template>
  <div class="row">
    <div class="col-12 col-md-8 col-lg-6 offset-0 offset-md-2 offset-lg-3">
      <div v-if="linkActive">
        <div v-if="paid">
          <b-alert show variant="success">
            <p class="m-5 text-center">Invoice <b>{{ invoiceNumber }}</b> for order <b>{{ order.order_id }}</b> and amount
              <b>{{ invoiceAmount | priceFormat() | withCurrency(currency) }}</b> was successfully paid.
              <br />Thank you</p>
          </b-alert>
        </div>
        <div v-else>
          <div class="row">
            <div class="col-12">
              <h2>{{ trans.get('link.order_summary') }}</h2>
              <div v-for="item in order.products">
                <div class="row">
                  <div class="col-9">{{ item.concept }}</div>
                  <div class="col-3 text-right">
                    <strong>{{ (item.unit_price * item.quantity) | priceFormat() | withCurrency(currency) }}</strong>
                  </div>
                </div>
                <div class="row">
                  <div class="col-6">{{ trans.get('link.quantity') }}: {{ item.quantity}}</div>
                </div>
              </div>
              <hr />
              <div class="row mt-2">
                <div class="col-6"><strong>{{ trans.get('link.total') }}</strong></div>
                <div class="col-6 text-right">
                  <strong>{{ invoiceAmount | priceFormat() | withCurrency(currency) }}</strong>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <h2 class="mt-5">{{ trans.get('link.credit_card_payment') }}</h2>
              <b-alert show variant="danger" v-if="paymentErrors" id="cardErrorMessage">
                <p>
                  <strong>{{ trans.get('link.important') }}:</strong>
                  {{ paymentErrors }}
                </p>
                <p>Invoice was <b>not</b> paid. <b>Please check your card details.</b></p>
                <p>You can try to pay it again</p>
              </b-alert>
              <div class="row">
                <div class="col-12">
                  <label class="w-100">
                    <span>{{ trans.get('link.credit_card_number') }}</span>
                    <div id="card-number-element" :class="{ field: true, 'stripe-element-custom': true, disabled: loading }">
                    </div>
                    <div class="card-errors" role="alert">{{ this.cardNumberErrors }}</div>
                  </label>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6 col-12">
                  <label class="w-100">
                    <span>{{ trans.get('link.expiry_date') }}</span>
                    <div id="card-expiry-element" :class="{ field: true, 'stripe-element-custom': true, disabled: loading }">
                    </div>
                    <div class="card-errors" role="alert">{{ this.cardExpiryErrors }}</div>
                  </label>
                </div>
                <div class="col-md-6 col-12">
                  <label class="w-100">
                    <span>{{ trans.get('link.cvc_number') }}</span>
                    <div id="card-cvc-element" :class="{ field: true, 'stripe-element-custom': true, disabled: loading }">
                    </div>
                    <div class="card-errors" role="alert">{{ this.cardCvcErrors }}</div>
                  </label>
                </div>
              </div>
              <div class="row">
                <div class="col-12 text-center">
                  <button
                      type="button"
                      @click="pay"
                      class="btn btn-sm my-3"
                      :disabled="!isButtonActive"
                  >
                    {{ trans.get('link.pay') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <b-alert show variant="danger">
          <p class="m-5 text-center">This payment link is no longer active</p>
        </b-alert>
      </div>
    </div>
  </div>
</template>

<script>

export default {
  name: 'StripePayment',
  data() {
    return {
      style: {
        base: {
          color: '#32325d',
          fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
          fontSmoothing: 'antialiased',
          fontSize: '16px',
          '::placeholder': {
            color: '#aab7c4'
          }
        },
        invalid: {
          color: '#fa755a',
          iconColor: '#fa755a'
        }
      },
      cardNumber: null,
      cardNumberCompleted: false,
      cardNumberErrors: null,
      cardExpiry: null,
      cardExpiryCompleted: false,
      cardExpiryErrors: null,
      cardCvc: null,
      cardCvcCompleted: false,
      cardCvcErrors: null,
      stripe: null,
      paymentErrors: false,
      loading: true,
      invoiceNumber: window.invoiceNumber,
      invoiceAmount: window.invoiceAmount,
      order: window.order,
      currency: window.currency,
      paid: false,
    }
  },
  computed: {
    isButtonActive() {
      return this.cardNumberCompleted
          && this.cardExpiryCompleted
          && this.cardCvcCompleted;
    },
    linkActive() {
      return window.stripePublicKey !== undefined;
    }
  },
  methods: {
    cardNumberErrorsHandler(event) {
      this.cardNumberErrors = event.error ? event.error.message : null;
    },
    cardExpiryErrorsHandler(event) {
      this.cardExpiryErrors = event.error ? event.error.message : null;
    },
    cardCvcErrorsHandler(event) {
      this.cardCvcErrors = event.error ? event.error.message : null;
    },
    pay() {
      const root = this;
      root.loading = true;
      root.paymentErrors = null;
      root.stripe.confirmCardPayment(
          window.stripeClientSecret,
          {
            payment_method: {
              card: root.cardNumber
            }
          }
      ).then((result) => {
        if (result.error) {
          root.paymentErrors = result.error.message;
          root.loading = false;
          setTimeout(function () {
            window.scrollTo({
              top: document.getElementById('cardErrorMessage').style.top,
              left: 0,
              behavior: "smooth",
            });
          }, 500);
        } else {
          root.paid = true;
        }
      });
    },
    setCardElement() {
      const root = this;
      root.stripe = Stripe(window.stripePublicKey);
      const elements = root.stripe.elements();
      root.cardNumber = elements.create('cardNumber', {
        style: this.style,
        placeholder: 'Card number',
      });
      root.cardNumber.mount('#card-number-element');
      root.cardNumber.on('change', function (event) {
        root.cardNumberCompleted = event.complete;
        root.cardNumberErrorsHandler(event);
      });
      root.cardExpiry = elements.create('cardExpiry', {
        style: this.style,
        placeholder: 'MM/YY',
      });
      root.cardExpiry.mount('#card-expiry-element');
      root.cardExpiry.on('change', function (event) {
        root.cardExpiryCompleted = event.complete;
        root.cardExpiryErrorsHandler(event);
      });
      root.cardCvc = elements.create('cardCvc', {
        style: root.style,
        placeholder: 'CVC',
      });
      root.cardCvc.mount('#card-cvc-element');
      root.cardCvc.on('change', function (event) {
        root.cardCvcCompleted = event.complete;
        root.cardCvcErrorsHandler(event);
      });
    },
  },
  mounted() {
    if (this.linkActive) {
      this.setCardElement();
    }
    this.loading = false;
  }
};
</script>

<style scoped>
.StripeElement, .stripe-element-custom {
  box-sizing: border-box;
  border: 1px solid #e4e4e4;
  border-radius: 3px;
  padding: 11px 0 11px 11px;
}

.StripeElement--focus {
  border-color: #a1a1a1;
  box-shadow: 0 0 2px 2px #4d90fe;
}

.card-errors {
  color: #eb1c26;
}

div.field {
  background-color: #fff;
}

div.field.disabled {
  cursor: default;
  background-color: #E9ECEF;
}


</style>
